import { AfterViewInit, Component, HostListener, OnDestroy, OnInit } from '@angular/core'
import { combineLatest, Subscription } from 'rxjs'
import { ViewportScroller } from '@angular/common'
import { CmsEnvironmentService } from './mb/cms/services/cms-environment.service'
import { MbDocumentService } from './mb/cms/services/mb-document-service/mb-document.service'
import { CmsService, EventService, Page, WindowRef } from '@spartacus/core'
import { CopyEvent } from './mb/cms/components/shared/gtm-event/copy-event.events'
import { AnalyticsEvent } from './mb/analytics/events/analytics.event'
import { MbConstants } from './mb/shared/mb-constants'
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router'
import { filter, map, withLatestFrom } from 'rxjs/operators'
import { MbEventListenerService } from './mb/cms/services/mb-event-listener/event-listener.service'
import { AnchorRenderedEvent } from './mb/cms/components/shared/events/anchor-rendered-event'

@Component({
  selector: 'mb-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnDestroy, AfterViewInit, OnInit {

  title = 'miltenyiwebstore'
  subscription = new Subscription()
  constants = MbConstants
  productUrl: string = ''
  observer: MutationObserver | undefined

  constructor (private router: Router,
               private activeRoute: ActivatedRoute,
               private viewportScroller: ViewportScroller,
               private cmsEnvService: CmsEnvironmentService,
               private documentService: MbDocumentService,
               private windowRef: WindowRef,
               private events: EventService,
               private eventListenerService: MbEventListenerService,
               private cmsService: CmsService) {
  }

  ngOnInit (): void {
    this.subscription.add(this.eventListenerService.retrieve()
      .pipe(
        filter(event => event && event.event instanceof AnchorRenderedEvent),
        map(event => event.event as AnchorRenderedEvent),
        withLatestFrom(this.activeRoute.fragment),
        filter(([event, fragment]) => event.anchor === fragment))
      .subscribe(([event]) => this.windowRef.document.querySelector(`#${event.anchor}`)?.scrollIntoView({ behavior: 'smooth' })))
  }

  @HostListener('window:copy', ['$event'])
  onCopy (event?: ClipboardEvent) {
    event?.preventDefault()
    const { defaultView } = this.windowRef.document
    const selection = defaultView?.getSelection()?.toString()
    const isProductTitle = defaultView?.getSelection()?.focusNode?.parentElement?.getAttribute(this.constants.DATA_ANALYTICS) === this.constants.PRODUCT_TITLE
    if (selection) {
      try {
        let obj
        let eventActionBool = false
        if (this.isProductCode(selection)) {
          obj = { code: selection }
          eventActionBool = true
        } else {
          obj = JSON.parse(selection)
        }
        event?.clipboardData?.setData('text/plain', obj.code || '')
        this.events.dispatch(
          new AnalyticsEvent(MbConstants.PRODUCT_SKU, this.constants.COPY_ORDER_NUMBER, (eventActionBool ? '' : `${obj.name?.replace(/<[^>]*>/g, '')} -' +
            ' ${obj.code}`), `${obj.code}`))
      } catch (_) {
        if (isProductTitle) {
          this.events.dispatch(new AnalyticsEvent(this.constants.PRODUCT_SKU, this.constants.COPY_PRODUCT_IDENTIFIER, selection))
        } else {
          this.events.dispatch(new CopyEvent(selection))
        }
        event?.clipboardData?.setData('text/plain', selection)
      }
    }
  }

  ngAfterViewInit (): void {
    this.subscription.add(
      combineLatest([this.router.events, this.activeRoute.fragment, this.activeRoute.queryParams, this.cmsService.getCurrentPage()])
        .pipe(filter(([event]) => event instanceof NavigationEnd))
        .subscribe(([_, fragment, queryParams, currentPage]: [any, string | null, Params, Page]) => {
          const isProductPage = currentPage?.pageId?.toLowerCase() === MbConstants.PRODUCT_DETAILS_PAGE_ID.toLowerCase()
            && !currentPage?.slots?.['PlaceholderContentSlot']?.components?.length
          const isSearchPage: boolean = currentPage?.pageId?.toLowerCase() === MbConstants.SEARCH_PAGE_ID.toLowerCase()
          if (!isProductPage) {
            if (fragment) {
              this.addAnchorScrollObserver(fragment)
            } else if (queryParams && Object.keys(queryParams).length) {
              if (isSearchPage) {
                this.viewportScroller.scrollToPosition([0, 0])
              }
            } else {
              this.viewportScroller.scrollToPosition([0, 0])
            }
          } else if (isProductPage) {
            const url = this.router.url ?? ''
            const currentProductUrl = url.slice(url.lastIndexOf(this.constants.SLASH_PRODUCTS_URL)
              + this.constants.SLASH_PRODUCTS_URL.length, url.indexOf(this.constants.HASH_SYMBOL))

            if (!this.productUrl || this.productUrl !== currentProductUrl) {
              this.viewportScroller.scrollToPosition([0, 0])
              this.productUrl = currentProductUrl
            }
          }
        })
    )

    this.subscription.add(this.cmsEnvService.isFirstSpiritPreview$
      .pipe(
        filter(isPreview => isPreview),
        map(() => this.documentService.querySelector('body')),
        filter(body => !!body?.classList))
      .subscribe(body => body?.classList?.add('preview')))
  }

  addAnchorScrollObserver (anchor: string) {
    this.observer = new MutationObserver(mutations => mutations
      .filter(mutation => mutation.type === 'childList')
      .map(mutation => Array.from(mutation.addedNodes)
        .filter(node => node instanceof HTMLElement && node.id === anchor)
        .shift())
      .filter(anchorElement => anchorElement)
      .forEach(() => setTimeout(() => this.windowRef.document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth' }), 500)))
    this.observer.observe(this.windowRef.document.body, { childList: true, subtree: true })
  }

  isProductCode (selection: string) {
    return (/^\d{3}-\d{3}-\d{3}$/.test(selection))
  }

  ngOnDestroy (): void {
    this.subscription.unsubscribe()
    this.observer?.disconnect()
  }
}
